// src/components/MemeSubmissionModal.tsx

import { useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

const client = generateClient();

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function MemeSubmissionModal({ isOpen, onClose }: Props) {
  const [textContent, setTextContent] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!agreedToTerms) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const session = await fetchAuthSession({ forceRefresh: true });
      const userId = session.tokens?.idToken?.payload.sub as string;

      if (!userId) throw new Error('Not authenticated');

      // Trim only — React handles XSS at render time, DynamoDB stores raw text
      const content = textContent.trim().slice(0, 500);

      if (content.length < 10) {
        setError('Please enter at least 10 characters');
        return;
      }

      await client.graphql({
        query: `
          mutation CreateMemeSubmission($input: CreateMemeSubmissionInput!) {
            createMemeSubmission(input: $input) {
              userId
              submissionDate
            }
          }
        `,
        variables: {
          input: {
            userId,
            submissionDate: today,
            type: 'TEXT',
            content,
            status: 'PENDING',
          },
        },
      });

      setSubmitted(true);

      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setTextContent('');
        setAgreedToTerms(false);
        setError(null);
      }, 2000);

    } catch (err: any) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // ----- Brand presentation tokens (inline) — match the rest of the app -----
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  const cardStyle: React.CSSProperties = {
    position: "relative",
    background: "#FFFFFF",
    border: "3px solid #1A1A1A",
    borderRadius: 20,
    boxShadow: "6px 6px 0px #1A1A1A",
    padding: "28px 24px",
    maxWidth: 380,
    width: "90%",
    margin: "0 auto",
    boxSizing: "border-box",
  };

  const closeBtnStyle: React.CSSProperties = {
    position: "absolute",
    top: 16,
    right: 16,
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 20,
    color: "#1A1A1A",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    lineHeight: 1,
    padding: 0,
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 22,
    fontWeight: 700,
    color: "#1A1A1A",
    margin: 0,
    marginBottom: 4,
  };

  const subtitleStyle: React.CSSProperties = {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 14,
    color: "#E05A3A",
    fontWeight: 600,
    margin: 0,
    marginBottom: 20,
  };

  const errorStyle: React.CSSProperties = {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 13,
    color: "#C44B2E",
    background: "rgba(255, 240, 235, 1)",
    border: "1.5px solid rgba(196, 75, 46, 0.4)",
    borderRadius: 12,
    padding: 12,
    marginBottom: 16,
  };

  const textareaStyle: React.CSSProperties = {
    width: "100%",
    minHeight: 100,
    border: "2px solid #E05A3A",
    borderRadius: 12,
    padding: 12,
    fontFamily: "'Nunito', sans-serif",
    fontSize: 14,
    color: "#1A1A1A",
    background: "#FDF6EE",
    outline: "none",
    boxSizing: "border-box",
    resize: "vertical",
  };

  const charCountStyle: React.CSSProperties = {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 12,
    color: "#888",
    textAlign: "right",
    marginTop: 4,
    marginBottom: 16,
  };

  const checkboxLabelStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    fontFamily: "'Nunito', sans-serif",
    fontSize: 13,
    color: "#444",
    lineHeight: 1.45,
    marginTop: 12,
    marginBottom: 16,
    cursor: "pointer",
  };

  const submitDisabled =
    !agreedToTerms || isSubmitting || !textContent.trim();

  const submitBtnStyle: React.CSSProperties = {
    width: "100%",
    background: submitDisabled ? "#cccccc" : "#E05A3A",
    color: submitDisabled ? "#888888" : "#FFFFFF",
    border: "3px solid #1A1A1A",
    borderRadius: 100,
    boxShadow: submitDisabled ? "none" : "4px 4px 0px #1A1A1A",
    padding: 14,
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 18,
    fontWeight: 700,
    cursor: submitDisabled ? "not-allowed" : "pointer",
    boxSizing: "border-box",
    transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
  };

  if (submitted) {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <h2 style={{ ...titleStyle, textAlign: "center", marginBottom: 8 }}>
            ✅ Submitted!
          </h2>
          <p
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: 14,
              color: "#444",
              textAlign: "center",
              margin: 0,
            }}
          >
            You can submit again tomorrow
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <button
          style={closeBtnStyle}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <h2 style={titleStyle}>Got a Good Crushhh Hint</h2>
        <p style={subtitleStyle}>Best ones win a free lifetime membership.</p>

        {error && (
          <div style={errorStyle}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <textarea
            placeholder={'e.g. "added my person on Crushhh, deleting this in an hour add me back"'}
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            maxLength={500}
            rows={4}
            style={textareaStyle}
          />
          <div style={charCountStyle}>
            {textContent.length}/500
          </div>
        </div>

        <label style={checkboxLabelStyle}>
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            style={{ marginTop: 2, accentColor: "#E05A3A", cursor: "pointer" }}
          />
          <span>
            I confirm this is my original content and I grant CRUSHHH permission to use, modify, and publish it.
          </span>
        </label>

        <button
          style={submitBtnStyle}
          onClick={handleSubmit}
          disabled={submitDisabled}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  );
}
